const DISMISS_KEY = 'pwa-install-dismissed';
const INSTALLED_KEY = 'pwa-installed';

let deferredPrompt = null;
const installStateListeners = new Set();

const readInstalledFlag = () => {
    try {
        return localStorage.getItem(INSTALLED_KEY) === '1';
    } catch {
        return false;
    }
};

const notifyInstallState = (installed) => {
    installStateListeners.forEach((listener) => listener(installed));
};

export const subscribeInstallState = (listener) => {
    installStateListeners.add(listener);
    return () => installStateListeners.delete(listener);
};

export const forgetInstalled = () => {
    let wasInstalled = false;
    try {
        wasInstalled = localStorage.getItem(INSTALLED_KEY) === '1';
        localStorage.removeItem(INSTALLED_KEY);
        if (wasInstalled) localStorage.removeItem(DISMISS_KEY);
    } catch {
        /* si no se puede borrar la marca, el próximo chequeo vuelve a intentarlo */
    }
    return wasInstalled;
};

export const isStandalone = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches
        || window.matchMedia('(display-mode: fullscreen)').matches
        || window.matchMedia('(display-mode: window-controls-overlay)').matches
        || window.navigator.standalone === true;
};

export const rememberInstalled = () => {
    try {
        localStorage.setItem(INSTALLED_KEY, '1');
    } catch {
        /* si el navegador no guarda la marca, el aviso se decide en cada visita */
    }
};

export const alreadyInstalled = () => {
    if (isStandalone()) {
        rememberInstalled();
        return true;
    }
    return readInstalledFlag();
};

export const queryInstalled = async () => {
    if (isStandalone()) {
        rememberInstalled();
        notifyInstallState(true);
        return true;
    }

    if (typeof navigator.getInstalledRelatedApps === 'function') {
        try {
            const apps = await navigator.getInstalledRelatedApps();
            const installed = Array.isArray(apps) && apps.some((app) => app.platform === 'webapp');
            if (installed) {
                rememberInstalled();
            } else {
                forgetInstalled();
            }
            notifyInstallState(installed);
            return installed;
        } catch {
            /* el navegador no dejó consultar; se usa la última marca conocida */
        }
    }

    const installed = readInstalledFlag();
    notifyInstallState(installed);
    return installed;
};

export const wasInstallDismissed = () => {
    try {
        return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
        return false;
    }
};

export const dismissInstallPrompt = () => {
    try {
        localStorage.setItem(DISMISS_KEY, '1');
    } catch {
        /* el aviso vuelve en la próxima visita si el navegador no guarda la preferencia */
    }
};

export const isIosSafari = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    const ios = /iphone|ipad|ipod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const safari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
    return ios && safari;
};

export const promptInstall = async () => {
    if (!deferredPrompt) return null;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    if (choice?.outcome === 'accepted') rememberInstalled();
    return choice;
};

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        deferredPrompt = event;
        forgetInstalled();
        notifyInstallState(false);
    });

    window.addEventListener('appinstalled', () => {
        rememberInstalled();
        deferredPrompt = null;
        notifyInstallState(true);
    });
}
